// Договор за наем по образеца: преглед на попълнените полета с
// възможност за корекция преди генериране. Липсващи данни се показват
// ясно; генериране с празни полета е възможно само с изричен избор.

import React, { useCallback, useState } from 'react';
import { Linking, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { api, API_URL } from '../../api';
import { showError, showSuccess } from '../../toast';
import { Button, Card, COLORS, Input, Label, Muted, Screen, Title } from '../../ui';
import { t } from '../../i18n';

// Полетата в прегледа, групирани по раздел на договора
type Field = { key: string; label: string; placeholder?: string };
// Функция, а не константа — етикетите се преизчисляват при смяна на езика
const SECTIONS = (): { title: string; fields: Field[] }[] => [
  {
    title: t('Общи'),
    fields: [
      { key: 'today', label: t('Дата на договора') },
      { key: 'city', label: t('Град на сключване') },
    ],
  },
  {
    title: t('Наемодател'),
    fields: [
      { key: 'landlordName', label: t('Име, презиме и фамилия (или фирма)') },
      { key: 'landlordEgn', label: t('ЕГН / ЕИК') },
      { key: 'landlordIdCard', label: t('Лична карта №') },
      { key: 'landlordIdIssued', label: t('Издадена на'), placeholder: t('дд.мм.гггг') },
      { key: 'landlordIdIssuer', label: t('От РПУ на МВР — град'), placeholder: t('напр. Пловдив') },
      { key: 'landlordAddress', label: t('Адрес') },
    ],
  },
  {
    title: t('Наемател'),
    fields: [
      { key: 'tenantName', label: t('Име, презиме и фамилия') },
      { key: 'tenantEgn', label: t('ЕГН') },
      { key: 'tenantIdCard', label: t('Лична карта №') },
      { key: 'tenantIdIssued', label: t('Издадена на'), placeholder: t('дд.мм.гггг') },
      { key: 'tenantIdIssuer', label: t('От РПУ на МВР — град'), placeholder: t('напр. София') },
      { key: 'tenantAddress', label: t('Адрес по лична карта') },
    ],
  },
  {
    title: t('Втори наемател (по избор)'),
    fields: [
      { key: 'tenant2Name', label: t('Име, презиме и фамилия') },
      { key: 'tenant2Egn', label: t('ЕГН') },
      { key: 'tenant2IdCard', label: t('Лична карта №') },
      { key: 'tenant2IdIssued', label: t('Издадена на'), placeholder: t('дд.мм.гггг') },
      { key: 'tenant2IdIssuer', label: t('От РПУ на МВР — град') },
      { key: 'tenant2Address', label: t('Адрес по лична карта') },
    ],
  },
  {
    title: t('Имот и условия'),
    fields: [
      { key: 'propertyLine1', label: t('Имотът (вид, площ, етаж)') },
      { key: 'propertyLine2', label: t('Имотът (адрес)') },
      { key: 'usage', label: t('Ще се използва за (Член 2)') },
      { key: 'users1', label: t('Ползватели на имота (Член 4)') },
      { key: 'rentAmount', label: t('Месечен наем (Член 5)') },
      { key: 'rentWords', label: t('Наемът с думи') },
      { key: 'payFromDay', label: t('Плаща се от число (Член 7)') },
      { key: 'payToDay', label: t('до число (Член 7)') },
      { key: 'depositAmount', label: t('Депозит (Член 18)') },
      { key: 'depositWords', label: t('Депозитът с думи') },
      { key: 'termMonths', label: t('Срок в месеци (Член 22)') },
      { key: 'startDate', label: t('Считано от (Член 22)') },
    ],
  },
];

export default function ContractScreen({ route, navigation }: any) {
  const { tenancyId } = route.params;
  const [fields, setFields] = useState<Record<string, string | null> | null>(null);
  const [missing, setMissing] = useState<string[]>([]);
  const [incomplete, setIncomplete] = useState<string[]>([]);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [showSecondTenant, setShowSecondTenant] = useState(false);

  useFocusEffect(
    useCallback(() => {
      api(`/documents/contract/data?tenancyId=${tenancyId}`)
        .then((r) => {
          setFields(r.fields);
          setMissing(r.missing);
          setIncomplete(r.incomplete);
        })
        .catch((e) => showError(e.message));
    }, [tenancyId])
  );

  const value = (key: string) => edits[key] ?? fields?.[key] ?? '';

  const generate = async (allowMissing: boolean) => {
    setLoading(true);
    try {
      // изпращат се само реално променените полета
      const overrides: Record<string, string> = {};
      for (const [k, v] of Object.entries(edits)) {
        if (v !== (fields?.[k] ?? '')) overrides[k] = v;
      }
      const res = await api('/documents/contract', {
        body: { tenancyId, ...(Object.keys(overrides).length ? { overrides } : {}), ...(allowMissing ? { allowMissing } : {}) },
      });
      showSuccess(t('Готово: {title}', { title: res.document.title }));
      Linking.openURL(`${API_URL}${res.document.fileUrl}`);
      navigation.goBack();
    } catch (e: any) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (!fields) {
    return (
      <Screen>
        <Title>{t('Договор за наем')}</Title>
        <Muted>{t('Зареждане…')}</Muted>
      </Screen>
    );
  }

  // ако данните вече съдържат втори наемател — секцията е отворена
  const hasTenant2 = !!(value('tenant2Name') || value('tenant2Egn'));

  return (
    <Screen>
      <Title>{t('Договор за наем')}</Title>
      <Muted>
        {t('По образеца, с данните от приложението. Непопълнените полета просто отпадат от готовия договор.')}
      </Muted>

      {missing.length > 0 && (
        <Card style={{ borderColor: COLORS.danger }}>
          <Label>{t('Липсват данни')}</Label>
          {missing.map((m) => (
            <Text key={m} style={{ color: COLORS.danger, marginBottom: 2 }}>
              • {m}
            </Text>
          ))}
          <Muted>{t('Наемателят ги допълва от Профил, или ги въведете ръчно долу.')}</Muted>
        </Card>
      )}
      {incomplete.length > 0 && (
        <Card style={{ borderColor: '#D97706' }}>
          <Label>{t('Ще останат празни в договора')}</Label>
          {incomplete.map((m) => (
            <Text key={m} style={{ color: '#D97706', marginBottom: 2 }}>
              • {m}
            </Text>
          ))}
        </Card>
      )}

      {SECTIONS().map((sec) => {
        // заглавието е преведено — секцията на втория наемател се познава по полетата
        const isSecond = sec.fields.some((f) => f.key === 'tenant2Name');
        const collapsed = isSecond && !showSecondTenant && !hasTenant2;
        return (
          <Card key={sec.title}>
            {collapsed ? (
              <Button
                title={t('+ Добави втори наемател (по избор)')}
                variant="secondary"
                onPress={() => setShowSecondTenant(true)}
              />
            ) : (
              <>
                <Label>{sec.title}</Label>
                {isSecond && (
                  <Muted>{t('Попълва се само при двама наематели — иначе блокът липсва от договора.')}</Muted>
                )}
                {sec.fields.map((f) => (
                  <View key={f.key}>
                    <Label>{f.label}</Label>
                    <Input
                      value={value(f.key)}
                      onChangeText={(t) => setEdits((e) => ({ ...e, [f.key]: t }))}
                      placeholder={f.placeholder ?? ''}
                    />
                  </View>
                ))}
              </>
            )}
          </Card>
        );
      })}

      <Button title={t('Генерирай договор (PDF)')} onPress={() => generate(false)} loading={loading} />
      {missing.length > 0 && (
        <Button
          title={t('Генерирай с празни полета')}
          variant="secondary"
          onPress={() => generate(true)}
          loading={loading}
        />
      )}
    </Screen>
  );
}
